import { useEffect, useState, useRef } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ref,
  push,
  onValue,
  update,
  set,
  onDisconnect,
  increment,
  off
} from "firebase/database";
import { db } from "../firebase";

import {
  FaPaperPlane,
  FaCheck,
  FaCheckDouble,
  FaCircle,
  FaSearch,
  FaEllipsisV,
  FaSmile,
  FaPlus,
  FaMicrophone,
  FaFileAlt,
  FaUserPlus,
  FaEyeSlash,
  FaBullseye,
  FaCircleNotch,
  FaExclamationTriangle,
  FaPhoneAlt,
  FaVideo,
  FaArrowLeft
} from "react-icons/fa";

import { askAI } from "../utils/gemini";

/* ===============================
   UNIVERSAL LINK AUTO-DETECTION
=============================== */
const renderMessageContent = (text) => {
  if (!text) return null;

  // Regex matching http://, https://, ftp://, www., or standard domain extensions (.com, .in, .org, .io, .ai, .dev, .net, etc.)
  const urlRegex = /((?:https?:\/\/|ftp:\/\/|www\.)[^\s<]+|(?:[a-zA-Z0-9-]+\.)+(?:com|org|net|io|co|in|dev|ai|app|gov|edu|me|tv|cc|xyz|info|biz|site|tech|online|store)(?:\/[^\s<]*)?)/gi;

  const parts = text.split(urlRegex);

  return parts.map((part, i) => {
    if (part && part.match(urlRegex)) {
      let href = part;
      if (!href.match(/^https?:\/\//i) && !href.match(/^ftp:\/\//i)) {
        href = `https://${href}`;
      }

      const lower = part.toLowerCase();
      let linkType = "general-link";

      if (lower.includes("youtube.com") || lower.includes("youtu.be")) {
        linkType = "yt-link";
      } else if (lower.includes("github.com") || lower.includes("gitlab.com")) {
        linkType = "github-link";
      } else if (lower.includes("twitter.com") || lower.includes("x.com")) {
        linkType = "twitter-link";
      } else if (lower.includes("instagram.com")) {
        linkType = "insta-link";
      } else if (lower.includes("facebook.com") || lower.includes("fb.watch")) {
        linkType = "fb-link";
      } else if (lower.includes("linkedin.com")) {
        linkType = "linkedin-link";
      } else if (lower.includes("reddit.com")) {
        linkType = "reddit-link";
      } else if (lower.includes("discord.gg") || lower.includes("discord.com")) {
        linkType = "discord-link";
      }

      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`chat-inline-link ${linkType}`}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

function ChatWindow({ currentUser, selectedUser, onBack }) {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [showAISuggestion, setShowAISuggestion] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const bottomRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const chatId =
    selectedUser &&
    (currentUser.uid < selectedUser.uid
      ? `${currentUser.uid}_${selectedUser.uid}`
      : `${selectedUser.uid}_${currentUser.uid}`);

  /* ===============================
     ONLINE / OFFLINE SYSTEM
  =============================== */
  useEffect(() => {
    const userStatusRef = ref(db, `status/${currentUser.uid}`);
    const connectedRef = ref(db, ".info/connected");

    const handleConnection = (snap) => {
      if (snap.val() === true) {
        onDisconnect(userStatusRef).set({
          state: "offline",
          lastChanged: Date.now()
        });

        set(userStatusRef, {
          state: "online",
          lastChanged: Date.now()
        });
      }
    };

    onValue(connectedRef, handleConnection);

    return () => {
      off(connectedRef, "value", handleConnection);
    };
  }, [currentUser.uid]);

  /* ===============================
     LISTEN OTHER USER STATUS
  =============================== */
  useEffect(() => {
    if (!selectedUser) return;

    const statusRef = ref(db, `status/${selectedUser.uid}`);

    const listener = onValue(statusRef, (snap) => {
      setUserStatus(snap.val());
    });

    return () => off(statusRef);
  }, [selectedUser]);

  /* ===============================
     LOAD MESSAGES
  =============================== */
  useEffect(() => {
    if (!chatId) return;

    const messagesRef = ref(db, `chats/${chatId}/messages`);

    const listener = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setMessages([]);
        return;
      }

      const msgList = Object.entries(data)
        .map(([id, value]) => ({
          id,
          ...value
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      setMessages(msgList);

      // Mark unseen messages as seen (only received ones)
      const updates = {};
      msgList.forEach((msg) => {
        if (
          msg.senderId !== currentUser.uid &&
          !msg.seen
        ) {
          updates[`${msg.id}/seen`] = true;
        }
      });

      if (Object.keys(updates).length > 0) {
        update(messagesRef, updates);
      }

      // Reset unread count
      update(
        ref(db, `chats/${chatId}/participants/${currentUser.uid}`),
        {
          unreadCount: 0,
          lastSeen: Date.now()
        }
      );
    });

    return () => off(messagesRef);
  }, [chatId, currentUser.uid]);

  /* ===============================
     AUTO SCROLL
  =============================== */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ===============================
     TYPING SYSTEM (FIXED)
  =============================== */
  const handleTyping = (value) => {
    setMessage(value);
    
    // Auto-suggest Sampark AI on @
    if (value.endsWith("@")) {
      setShowAISuggestion(true);
    } else if (showAISuggestion && !value.includes("@")) {
      setShowAISuggestion(false);
    }

    if (!chatId) return;
    const typingRef = ref(db, `typing/${chatId}/${currentUser.uid}`);
    set(typingRef, true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      set(typingRef, false);
    }, 2000);
  };

  const selectAISuggestion = () => {
    setMessage("@Sampark AI ");
    setShowAISuggestion(false);
  };

  useEffect(() => {
    if (!chatId || !selectedUser) return;

    const typingRef = ref(
      db,
      `typing/${chatId}/${selectedUser.uid}`
    );

    const listener = onValue(typingRef, (snap) => {
      setIsTyping(!!snap.val());
    });

    return () => off(typingRef);
  }, [chatId, selectedUser]);

  /* ===============================
     SEND MESSAGE
  =============================== */
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !chatId) return;

    const currentMsg = message.trim();
    const isAIMention = currentMsg.startsWith("@Sampark AI");
    
    const messagesRef = ref(db, `chats/${chatId}/messages`);

    // 1. Send User Message
    push(messagesRef, {
      text: currentMsg,
      senderId: currentUser.uid,
      timestamp: Date.now(),
      seen: false
    });

    update(ref(db, `chats/${chatId}`), {
      lastMessage: currentMsg,
      lastMessageTime: Date.now(),
      lastSender: currentUser.uid
    });

    // Reset input immediately
    setMessage("");
    set(ref(db, `typing/${chatId}/${currentUser.uid}`), false);

    // 2. Handle AI Mention logic
    if (isAIMention) {
      const query = currentMsg.replace("@Sampark AI", "").trim();
      if (query) {
        // 🧠 Context Injection: Capture last 5 messages for history
        const threadHistory = messages.slice(-5).map(m => ({
          role: m.senderId === "SamparkAI" || m.senderId === "ai" ? "model" : "user",
          parts: [{ text: m.text }]
        }));

        const aiResponse = await askAI(query, threadHistory);
        
        push(messagesRef, {
          text: aiResponse,
          senderId: "SamparkAI",
          timestamp: Date.now(),
          seen: true
        });

        // Update last message
        update(ref(db, `chats/${chatId}`), {
          lastMessage: "Sampark AI: " + aiResponse.slice(0, 30) + "...",
          lastMessageTime: Date.now(),
          lastSender: "SamparkAI"
        });
      }
    }

    // 3. Update Unread Count for Receiver (if not AI only)
    if (!isAIMention) {
      update(
        ref(db, `chats/${chatId}/participants/${selectedUser.uid}`),
        {
          unreadCount: increment(1)
        }
      );
    }
  };

  /* ===============================
     EMPTY STATE
  =============================== */
  if (!selectedUser) {
    return (
      <div className="chat-window empty-chat desktop-empty" style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
        <div 
          className="chat-header empty-chat-header-bar"
          style={{
            height: '60px',
            minHeight: '60px',
            maxHeight: '60px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            background: 'var(--panel-header-bg)',
            borderBottom: '1px solid var(--border-light)',
            boxSizing: 'border-box',
            flexShrink: 0,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 50
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(0, 168, 132, 0.15)', border: '1px solid rgba(0, 168, 132, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00a884', fontSize: '13px', fontWeight: 'bold' }}>
              BC
            </div>
            <span style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', letterSpacing: '0.3px' }}>
              Bharat Connect v3.0 • Powered by Sampark AI
            </span>
          </div>
          <span style={{ fontSize: '12px', background: 'rgba(0, 168, 132, 0.1)', color: '#00a884', padding: '4px 10px', borderRadius: '12px', fontWeight: '500', border: '1px solid rgba(0, 168, 132, 0.2)' }}>
            🔒 End-to-End Encrypted
          </span>
        </div>

        <div className="empty-buttons-container" style={{ margin: 0 }}>
          <div className="empty-btn">
            <div className="empty-btn-icon-wrapper">
              <FaFileAlt className="empty-btn-icon" />
            </div>
            <span>Send document</span>
          </div>
          <div className="empty-btn">
            <div className="empty-btn-icon-wrapper">
              <FaUserPlus className="empty-btn-icon" />
            </div>
            <span>Add contact</span>
          </div>
          <div className="empty-btn ai-special-btn" onClick={() => window.location.href = "/SamparkAI"}>
            <div className="empty-btn-icon-wrapper">
              <FaCircleNotch className="empty-btn-icon aiPulse" />
            </div>
            <span>Ask Sampark AI</span>
          </div>
        </div>
      </div>
    );
  }

  /* ===============================
     UI
  =============================== */
  return (
    <div className="chat-window">
      {/* HEADER */}
      <div 
        className="chat-header"
        style={{
          height: '60px',
          minHeight: '60px',
          maxHeight: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          background: 'var(--panel-header-bg)',
          borderBottom: '1px solid var(--border-light)',
          boxSizing: 'border-box',
          flexShrink: 0
        }}
      >
        <div 
          className="chat-header-left"
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            gap: '12px'
          }}
        >
          {onBack && (
            <FaArrowLeft 
              style={{ fontSize: '18px', marginRight: '6px', cursor: 'pointer', color: 'var(--icon-color)' }} 
              onClick={onBack} 
              title="Back to Chat List" 
            />
          )}

          <div 
            className="avatar"
            style={{
              width: '40px',
              height: '40px',
              minWidth: '40px',
              minHeight: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 600,
              color: '#00a884',
              background: 'rgba(0, 168, 132, 0.15)',
              border: '1px solid rgba(0, 168, 132, 0.3)',
              margin: 0,
              flexShrink: 0
            }}
          >
            {selectedUser.photoURL ? (
              <img src={selectedUser.photoURL} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <span>{selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : "U"}</span>
            )}
          </div>

          <div 
            className="chat-header-info"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              margin: 0,
              padding: 0
            }}
          >
            <h3 
              style={{
                margin: 0,
                padding: 0,
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                lineHeight: 1.2
              }}
            >
              {selectedUser.name}
            </h3>
            <div 
              className="status"
              style={{
                margin: '2px 0 0 0',
                padding: 0,
                fontSize: '12px',
                color: 'var(--text-secondary)',
                lineHeight: 1.2
              }}
            >
              {selectedUser.uid === currentUser.uid || selectedUser.isSelf
                ? "Message yourself"
                : userStatus?.state === "online"
                ? "Online"
                : "Offline"}
            </div>
          </div>
        </div>

        <div 
          className="chat-header-actions"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            color: 'var(--icon-color)',
            fontSize: '18px'
          }}
        >
          <FaSearch title="Search" style={{ cursor: 'pointer' }} />
          <FaPhoneAlt title="Voice Call" style={{ cursor: 'pointer' }} />
          <FaVideo title="Video Call" style={{ cursor: 'pointer' }} />
          <FaEllipsisV title="Menu" style={{ cursor: 'pointer' }} />
        </div>
      </div>

      {/* MESSAGES */}
      <div className="chat-messages">
        {messages.map((msg) => {
          const isSent = msg.senderId === currentUser.uid;
          const isAI = msg.senderId === "SamparkAI";

          return (
            <div
              key={msg.id}
              className={`chat-bubble ${
                isAI ? "received ai-mention-bubble" : isSent ? "sent" : "received"
              }`}
            >
              {isAI && (
                <div className="ai-header-badge">
                  <FaBullseye style={{ fontSize: '14px' }} className="aiPulse" />
                  <span>SAMPARK AI</span>
                </div>
              )}
              {msg.text.includes("ERROR_PREFIX") ? (
                <>
                  <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                    <FaExclamationTriangle style={{ marginRight: '5px' }} />
                    System Error Detected
                  </span>
                  <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#ff4d4f' }}>
                    {msg.text.replace("ERROR_PREFIX", "")}
                  </div>
                </>
              ) : isAI ? (
                <div className="ai-msg-text-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {msg.text}
                  </ReactMarkdown>
                </div>
              ) : (
                <span>{renderMessageContent(msg.text)}</span>
              )}

              <div className="bubble-meta">
                <span className="bubble-time">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                {isSent && (
                  <span className="seen-icon">
                    {msg.seen ? <FaCheckDouble /> : <FaCheck />}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="typing-indicator">
            Typing...
          </div>
        )}

        <div ref={bottomRef}></div>
      </div>

      {/* INPUT */}
      <form
        onSubmit={sendMessage}
        className="chat-input-area"
        style={{ position: 'relative' }}
      >
        {showAISuggestion && (
          <div 
            className="ai-suggestion-bubble"
            onClick={selectAISuggestion}
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '20px',
              marginBottom: '10px',
              background: '#161b19',
              border: '1px solid #00a67d',
              borderRadius: '12px',
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
              animation: 'slideInUp 0.2s ease',
              zIndex: 100
            }}
          >
            <FaBullseye style={{ color: '#00a67d' }} className="aiPulse" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>Sampark AI</span>
              <span style={{ fontSize: '11px', color: '#8b9994' }}>Mention to invoke intelligence</span>
            </div>
          </div>
        )}

        <div className="input-icons">
          <FaSmile title="Emoji" />
          <FaPlus title="Attach" />
        </div>

        <input
          value={message}
          onChange={(e) => handleTyping(e.target.value)}
          placeholder="Type a message..."
          onClick={() => setShowAISuggestion(false)}
        />

        <button type={message.trim() ? "submit" : "button"} className="mic-send-btn">
          {message.trim() ? <FaPaperPlane title="Send" /> : <FaMicrophone title="Record" />}
        </button>
      </form>
    </div>
  );
}

export default ChatWindow;
